import React from "react";
import "./Process.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faPenToSquare,
  faRocket,
  faChartLine,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const steps = [
  {
    title: "Discovery & audit",
    description:
      "We review your goals, current channels, analytics, creative assets, and constraints. Outputs may include a findings deck and prioritized opportunity list.",
    icon: faClipboardList,
    bullets: ["Stakeholder intake", "Account & site access", "Baseline metrics"],
  },
  {
    title: "Strategy & scope",
    description:
      "We agree on channel mix, timelines, deliverables, and fees in a written proposal or SOW. Assumptions and exclusions are spelled out to reduce surprises.",
    icon: faPenToSquare,
    bullets: ["KPIs & reporting", "Budget pacing", "Legal/compliance flags"],
  },
  {
    title: "Build & launch",
    description:
      "Campaigns, content, tags, and landing updates go live with QA checklists. We coordinate with your team on brand, legal, and product approvals when needed.",
    icon: faRocket,
    bullets: ["Creative handoffs", "Tracking verification", "Launch comms"],
  },
  {
    title: "Optimize & report",
    description:
      "We monitor performance, run structured tests where appropriate, and share readable reports. Recommendations tie back to your goals—not vanity metrics alone.",
    icon: faChartLine,
    bullets: ["Weekly/biweekly updates", "Monthly reviews", "Change logs"],
  },
  {
    title: "Renew or transition",
    description:
      "At period end we recap learnings, document accounts, and support an orderly handoff if you move work in-house or to another partner.",
    icon: faHandshake,
    bullets: ["Asset export", "Admin access", "Knowledge transfer call"],
  },
];

const Process = () => {
  return (
    <section className="prc" aria-label="Delivery process">
      <div className="prc__wrap">
        <motion.div
          className="prc__head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <div>
            <div className="prc__badge">
              <span className="dot" aria-hidden="true" />
              How we deliver
            </div>

            <h2 className="prc__title">
              A process built for <span>accountability</span>.
            </h2>

            <p className="prc__sub">
              Steps flex by engagement type. Rush launches may compress phases; enterprise clients may add security or
              procurement reviews.
            </p>
          </div>

          <div className="prc__aside">
            <b>Your part:</b>
            <p>
              Timely feedback, accurate product info, and access to ad accounts and analytics help us move faster and
              stay compliant with platform rules.
            </p>
          </div>
        </motion.div>

        <div className="prc__grid">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              className="prc__card"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
              variants={fadeUp}
              transition={{ delay: i * 0.06 }}
            >
              <div className="prc__top">
                <div className="prc__step">
                  <span className="prc__stepDot" aria-hidden="true" />
                  Step {String(i + 1).padStart(2, "0")}
                </div>

                <div className="prc__icon" aria-hidden="true">
                  <FontAwesomeIcon icon={s.icon} />
                </div>
              </div>

              <h3 className="prc__h3">{s.title}</h3>
              <p className="prc__p">{s.description}</p>

              <ul className="prc__bullets">
                {s.bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
